import { Input } from '@mui/joy';

import { z } from 'zod';

import { CodeData } from '@/components/Code/Code';
import { MessageFields } from '@/types/schema';
import { tryParseJSON } from '@/utils/try-parse-json';

import {
  OpenAIFunctionCallKwargsSchema,
  OpenAIToolCallsKwargsSchema,
  ToolCallsKwargsSchema,
} from '../Message.utils';
import CodeFunctionCallOaiToolEditor from './CodeFunctionCallOaiToolEditor';
import CodeToolEditor from './CodeToolEditor';
import { INPUT_SX } from './constants';

const CodeFunctionCallEditor = ({
  value,
  onChange,
  readOnly,
  placeholder,
}: {
  value?:
    | MessageFields['additional_kwargs']
    | { tool_calls: MessageFields['tool_calls'] };
  onChange: (
    value:
      | MessageFields['additional_kwargs']
      | { tool_calls: MessageFields['tool_calls'] }
  ) => void;
  readOnly?: boolean;

  placeholder?: string;
}) => {
  const fnCall = OpenAIFunctionCallKwargsSchema.safeParse(value);

  if (fnCall.success) {
    return (
      <div>
        <Input
          placeholder="Variable name"
          className="font-mono"
          sx={{ ...INPUT_SX, py: 0, px: 0 }}
          value={fnCall.data?.function_call?.name ?? ''}
          readOnly={readOnly}
          onChange={(e) => {
            onChange({
              function_call: {
                ...fnCall.data?.function_call,
                name: e.target.value,
              },
            } satisfies z.infer<typeof OpenAIFunctionCallKwargsSchema>);
          }}
        />
        <CodeData
          readOnly={readOnly}
          value={tryParseJSON(fnCall.data.function_call.arguments, true)}
          onChange={(data) => {
            onChange({
              function_call: {
                ...fnCall.data?.function_call,
                arguments: JSON.stringify(data),
              },
            } satisfies z.infer<typeof OpenAIFunctionCallKwargsSchema>);
          }}
        />
      </div>
    );
  }

  const oaiToolCalls = OpenAIToolCallsKwargsSchema.safeParse(value);
  if (oaiToolCalls.success) {
    return (
      <CodeFunctionCallOaiToolEditor
        value={oaiToolCalls.data}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  const toolCalls = ToolCallsKwargsSchema.safeParse(value);
  if (toolCalls.success && toolCalls.data.tool_calls.length > 0) {
    return (
      <CodeToolEditor
        value={toolCalls.data}
        onChange={onChange}
        readOnly={readOnly}
      />
    );
  }

  return (
    <CodeData
      value={value}
      readOnly={readOnly}
      placeholder={placeholder}
      onChange={(value) => onChange(value)}
    />
  );
};

export default CodeFunctionCallEditor;
